import { IconButton, Stack, Typography, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';

import { AppAdaptedTooltip, MegaphoneIcon } from '@frontend-libs/portal';

import { preferenceClient } from '@portal-clients/preference';
import {
  HiddenInfoPanelModel,
  HiddenPreferencesModel,
} from '@portal-clients/user';

import { tCommon } from '@frontend/locales';
import { useOptionalUser } from '@frontend/utils';

import { CrossIcon } from '../icons';

export interface NewsTopInfoPanelProps {
  onHideTopInfoPanel: () => void;
  title: string;
  infoPanelType: HiddenInfoPanelModel;
}

export const NewsTopInfoPanel = (props: NewsTopInfoPanelProps) => {
  const theme = useTheme();
  const { user, updatePreferences } = useOptionalUser();

  const handleHideTopInfoBox = async () => {
    if (!user) {
      return;
    }

    props.onHideTopInfoPanel();

    const hiddenPreferences: HiddenPreferencesModel = {
      balances: user.preferences?.hidden?.balances ?? [],
      infoPanels: [
        ...(user.preferences?.hidden?.infoPanels ?? []),
        props.infoPanelType,
      ],
    };

    updatePreferences({
      hidden: hiddenPreferences,
    });

    await preferenceClient
      .set('hidden', {
        preference: hiddenPreferences,
      })
      .catch((e) => {
        captureException(e, {
          level: 'warning',
          extra: {
            message: 'Failed to update preferences to hide info panel',
          },
        });
      });
  };

  return (
    <Stack
      mt={4}
      p={1.5}
      direction="row"
      gap={1}
      justifyContent="space-between"
      alignItems="start"
      width="100%"
      sx={{
        backgroundColor: theme.palette.grey[100],
        borderRadius: '6px',
      }}
    >
      <MegaphoneIcon stroke={theme.palette.text.primary} />

      <Typography variant="subtitle2" component="p" alignSelf="center" flex={1}>
        {props.title}
      </Typography>

      {user && (
        <AppAdaptedTooltip title={tCommon.actions.hide()}>
          <IconButton
            sx={{ alignSelf: 'flex-start', padding: 0 }}
            onClick={() => handleHideTopInfoBox()}
            data-eventid="hide-top-info-panel"
            size="small"
          >
            <CrossIcon stroke={theme.palette.text.primary} />
          </IconButton>
        </AppAdaptedTooltip>
      )}
    </Stack>
  );
};
